import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { useApp } from "@/hooks/use-app-context";
import { ComplaintsHeader } from './ComplaintsHeader';
import { ComplaintsForm } from './ComplaintsForm';
import { ComplaintsHistory } from "@/features/complaints/ComplaintsHistory";
import { ComplaintsSidebar, isComplaintTab, type ComplaintTab } from "@/features/complaints";
import type { ComplaintType } from "./complaints.types";

const isComplaintType = (value: string | null): value is ComplaintType =>
  value === "bug" || value === "suggestion" || value === "other";

export default function ComplaintsPage() {
  const { isAuthenticated } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<ComplaintTab>(() => {
    const tab = searchParams.get("tab");
    if (tab === "history" && !isAuthenticated) return "submit";
    return isComplaintTab(tab) ? tab : "submit";
  });

  const [type, setType] = useState<ComplaintType>(() => {
    const t = searchParams.get("type");
    return isComplaintType(t) ? t : "other";
  });

  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "history" && !isAuthenticated) {
      updateParams({ tab: "submit" });
      setActiveTab("submit");
      return;
    }
    if (isComplaintTab(tab) && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [activeTab, searchParams, isAuthenticated, updateParams]);

  useEffect(() => {
    const t = searchParams.get("type");
    if (isComplaintType(t) && t !== type) {
      setType(t);
    }
  }, [type, searchParams]);

  const handleTabChange = (value: ComplaintTab) => {
    if (value === "history" && !isAuthenticated) return;
    setActiveTab(value);
    updateParams({ tab: value });
  };

  const handleTypeChange = (newType: ComplaintType) => {
    setType(newType);
    updateParams({ type: newType });
  };

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <ComplaintsHeader />
      <Tabs value={activeTab} onValueChange={(value) => handleTabChange(value as ComplaintTab)} className="space-y-6">
        <div className="grid gap-7 lg:grid-cols-[14rem_minmax(0,1fr)] lg:gap-10 xl:grid-cols-[16rem_minmax(0,1fr)] xl:gap-12">
          <ComplaintsSidebar activeTab={activeTab} onTabChange={handleTabChange} isAuthenticated={isAuthenticated} />
          <div className="min-w-0 space-y-6">
            <TabsContent value="submit" className="mt-0">
              <ComplaintsForm type={type} onTypeChange={handleTypeChange} />
            </TabsContent>
            <TabsContent value="history" className="mt-0">
              <ComplaintsHistory />
            </TabsContent>
          </div>
        </div>
      </Tabs>
    </div>
  );
}
